import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

export const useCategories = ({ activeApi, token, setConfirmationModal, onCategoryDeleted }) => {
  const [categories, setCategories] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [categoryPage, setCategoryPage] = useState(1);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    if (token) {
      if (!hasFetchedData.current) {
        hasFetchedData.current = true;
        activeApi.getCategories().then(setCategories).catch(error => console.error("Error fetching categories:", error));
      }
    } else {
      hasFetchedData.current = false;
    }
  }, [token, activeApi]);

  const addCategory = useCallback(async (category, andAnother) => {
    const isEditing = !!editingCategory;
    if (categories.find(c => c.title.toLowerCase() === category.title.toLowerCase() && c.id !== category.id)) {
      return { error: 'A category with this name already exists.' };
    }
    try {
      const savedCategory = isEditing
        ? await activeApi.updateCategoryAPI(editingCategory.id, category)
        : await activeApi.addCategoryAPI(category);

      if (isEditing) {
        setCategories(prev => prev.map(c => (c.id === savedCategory.id ? savedCategory : c)));
        setEditingCategory(null);
      } else {
        setCategories(prev => [...prev, savedCategory]);
      }
      if (!andAnother) {
        setIsCategoryModalOpen(false);
      }
      return { success: true, category: savedCategory };
    } catch (error) {
      return { error: error.message };
    }
  }, [categories, editingCategory, activeApi]);

  const deleteCategory = useCallback((categoryId) => {
    const categoryToDelete = categories.find(c => c.id === categoryId);
    const message = categoryToDelete
      ? `Are you sure you want to delete category '${categoryToDelete.title}'?`
      : 'Are you sure you want to delete this category?';

    setConfirmationModal({
      isOpen: true,
      message,
      onConfirm: () => {
        activeApi.deleteCategoryAPI(categoryId)
          .then(() => {
            const newCategories = categories.filter(c => c.id !== categoryId);
            setCategories(newCategories);
            const totalPages = Math.ceil(newCategories.length / 5);
            if (categoryPage > totalPages) {
              setCategoryPage(totalPages || 1);
            }
            if (onCategoryDeleted) {
              onCategoryDeleted(categoryId);
            }
          })
          .catch(error => console.error('Error deleting category:', error))
          .finally(() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { } }));
      }
    });
  }, [activeApi, categories, categoryPage, setConfirmationModal, onCategoryDeleted]);

  const startEditingCategory = useCallback((category) => {
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  }, []);

  const cancelEditingCategory = useCallback(() => {
    setEditingCategory(null);
    setIsCategoryModalOpen(false);
  }, []);

  const openAddCategoryModal = useCallback(() => {
    setEditingCategory(null);
    setIsCategoryModalOpen(true);
  }, []);

  const handleCategorySelection = useCallback((categoryId, operation = 'OR') => {
    setSelectedCategoryIds(prev => {
      const existingIndex = prev.findIndex(item => item.id === categoryId);
      if (existingIndex >= 0) {
        // If already selected with same operation, remove it (toggle)
        if (prev[existingIndex].operation === operation) {
          return prev.filter((_, index) => index !== existingIndex);
        }
        // If operation is different, update it
        const newSelection = [...prev];
        newSelection[existingIndex] = { id: categoryId, operation };
        return newSelection;
      }
      // If not selected, add it
      return [...prev, { id: categoryId, operation }];
    });
  }, []);

  const unselectAllCategories = useCallback(() => {
    setSelectedCategoryIds([]);
  }, []);

  const handleSearchChange = useCallback((term) => {
    setCategorySearchTerm(term);
    setCategoryPage(1);
  }, []);

  const filteredCategories = useMemo(() =>
    categories.filter(category =>
      category && category.title && category.title.toLowerCase().includes(categorySearchTerm.toLowerCase())
    ), [categories, categorySearchTerm]
  );

  return {
    categories,
    setCategories,
    filteredCategories,
    isCategoryModalOpen,
    setIsCategoryModalOpen,
    editingCategory,
    categorySearchTerm,
    categoryPage, setCategoryPage,
    selectedCategoryIds, setSelectedCategoryIds,
    addCategory,
    deleteCategory,
    startEditingCategory,
    cancelEditingCategory,
    openAddCategoryModal,
    handleCategorySelection,
    unselectAllCategories,
    handleSearchChange,
  };
};